/**
 * Authentication for the portal screen.
 *
 * The default provider keeps accounts in localStorage so the game is playable
 * with no backend at all. Passwords are salted and hashed with SHA-256 via
 * WebCrypto — enough that a stored profile is not plaintext, but this is a
 * local-only convenience store, not a security boundary. Swap in a real
 * provider (Supabase, Firebase, your own API) by passing an object with the
 * same four methods to `Auth`.
 */

const USERS_KEY = 'battlechess.users';
const SESSION_KEY = 'battlechess.session';

const read = (key, fallback) => {
  try {
    return JSON.parse(localStorage.getItem(key)) ?? fallback;
  } catch {
    return fallback;
  }
};
const write = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* Private-mode browsers may refuse writes; the session stays in memory. */
  }
};

const randomSalt = () =>
  Array.from(crypto.getRandomValues(new Uint8Array(16)))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');

async function hash(password, salt) {
  const bytes = new TextEncoder().encode(`${salt}:${password}`);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/** Constant-time-ish comparison so hash checks do not leak by timing. */
const sameHash = (a, b) => {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
};

const emptyRecord = (name) => ({
  name,
  stats: { wins: 0, losses: 0, draws: 0, games: 0 },
  createdAt: Date.now()
});

export const localProvider = {
  async signUp(name, password) {
    const key = name.trim().toLowerCase();
    if (key.length < 3) throw new Error('Name must be at least 3 characters.');
    if (password.length < 6) throw new Error('Password must be at least 6 characters.');

    const users = read(USERS_KEY, {});
    if (users[key]) throw new Error('That champion name is already taken.');

    const salt = randomSalt();
    users[key] = {
      ...emptyRecord(name.trim()),
      salt,
      hash: await hash(password, salt)
    };
    write(USERS_KEY, users);
    return { name: users[key].name, stats: users[key].stats };
  },

  async signIn(name, password) {
    const key = name.trim().toLowerCase();
    const users = read(USERS_KEY, {});
    const record = users[key];
    if (!record) throw new Error('No such champion. Enlist first.');
    const attempt = await hash(password, record.salt);
    if (!sameHash(attempt, record.hash)) throw new Error('Incorrect password.');
    return { name: record.name, stats: record.stats };
  },

  async signOut() {
    /* Nothing to revoke for the local provider. */
  },

  async saveStats(name, stats) {
    const key = name.trim().toLowerCase();
    const users = read(USERS_KEY, {});
    if (!users[key]) return;
    users[key].stats = stats;
    write(USERS_KEY, users);
  }
};

export class Auth {
  constructor(provider = localProvider) {
    this.provider = provider;
    this.user = read(SESSION_KEY, null);
    this.listeners = new Set();
  }

  onChange(fn) {
    this.listeners.add(fn);
    fn(this.user);
    return () => this.listeners.delete(fn);
  }

  emit() {
    for (const fn of this.listeners) fn(this.user);
  }

  setUser(user) {
    this.user = user;
    if (user) write(SESSION_KEY, user);
    else localStorage.removeItem(SESSION_KEY);
    this.emit();
  }

  async signUp(name, password) {
    this.setUser(await this.provider.signUp(name, password));
    return this.user;
  }

  async signIn(name, password) {
    this.setUser(await this.provider.signIn(name, password));
    return this.user;
  }

  /** Plays without an account; stats are kept for the session only. */
  playAsGuest() {
    this.setUser({ ...emptyRecord('Guest'), guest: true });
    return this.user;
  }

  async signOut() {
    await this.provider.signOut();
    this.setUser(null);
  }

  /**
   * Records a finished game and persists the updated tally.
   *
   * Against a real backend the increment here is only a local echo so the
   * portal updates immediately: recording the game is what actually moves
   * the server's figures, and `adoptStats` overwrites this with the
   * authoritative ones a moment later. `localProvider` has no server, so
   * there this tally IS the record.
   */
  async recordResult(result) {
    if (!this.user) return;
    const stats = { ...this.user.stats };
    stats.games++;
    if (result === 'win') stats.wins++;
    else if (result === 'loss') stats.losses++;
    else stats.draws++;
    this.user = { ...this.user, stats };
    write(SESSION_KEY, this.user);
    if (!this.user.guest) await this.provider.saveStats(this.user.name, stats);
    this.emit();
  }

  /** Replaces the local tally with the server's own, which outranks it. */
  adoptStats(stats) {
    if (!this.user || !stats) return;
    this.user = { ...this.user, stats };
    write(SESSION_KEY, this.user);
    this.emit();
  }
}
